import { forwardRef, useEffect, useMemo, useState } from "react";
import { submitCPETInfo } from "../../api/forms/cpet-info";
import { CPETInformation } from "../../api/forms/cpet-info/types";
import { Nullable } from "../../api/types";
import useDataInputForm, { FORM_IDS } from "../../hooks/useDataInputForm";
import useImperativeFormSubmit from "../../hooks/useImperativeFormSubmit";
import { FormHandle, InputTypes } from "../../types";
import Error from "../Error";
import DataInputGenericForm, {
    DataInputGenericFormProps,
} from "./DataInputGenericForm";

export default forwardRef<FormHandle<Nullable<CPETInformation>> | null, {}>(
    function CPETInfoForm(
        props,
        ref: React.ForwardedRef<FormHandle<Nullable<CPETInformation>> | null>
    ) {
        const { formData, loading, error, setFromData } = useDataInputForm({
            formId: FORM_IDS.CPET_INFO,
        });

        const [formValues, setFormValues] = useState<
            CPETInformation | null | undefined
        >();

        useEffect(() => {
            if (formData) {
                setFormValues(formData as CPETInformation);
            }
        }, [formData]);

        const { valuesErrors, submissionLoading } =
            useImperativeFormSubmit<CPETInformation>({
                formValues,
                formData,
                ref,
                onSubmitSuccess: setFromData,
                submitFunction: submitCPETInfo,
            });

        type FormFields =
            DataInputGenericFormProps<CPETInformation>["formFields"];

        const formFields = useMemo<FormFields>(
            (): FormFields =>
                !formData || !formValues
                    ? []
                    : [
                          {
                              id: "purpose",
                              label: "Reason for test",
                              value: formValues.purpose,
                              valueToDisplay: formValues.purpose,
                              originalValue: formData.purpose,
                              type: InputTypes.SELECT,
                              options: formValues.all_purposes,
                              error: valuesErrors?.purpose,
                              required: false,
                          },
                          {
                              id: "ergometer",
                              label: "Ergometer",
                              value: formValues.ergometer,
                              valueToDisplay: formValues.ergometer,
                              originalValue: formData.ergometer,
                              type: InputTypes.SELECT,
                              options: formValues.all_ergometers,
                              error: valuesErrors?.ergometer,
                              required: false,
                          },
                          {
                              id: "protocol",
                              label: "Protocol",
                              value: formValues.protocol,
                              valueToDisplay: formValues.protocol,
                              originalValue: formData.protocol,
                              type: InputTypes.SELECT,
                              options: (() => {
                                  let protocolList: string[] = [];
                                  if (formValues.ergometer === "Bicycle") {
                                      protocolList =
                                          formValues.all_protocols_bicycle;
                                  } else if (
                                      formValues.ergometer === "Treadmill"
                                  ) {
                                      protocolList =
                                          formValues.all_protocols_treadmill;
                                  } else {
                                      protocolList = formValues.all_protocols;
                                  }
                                  return protocolList;
                              })(),
                              error: valuesErrors?.protocol,
                              required: false,
                              disabled: !formValues.ergometer,
                          },
                      ],
            [formData, formValues, valuesErrors]
        );

        if (error) {
            return <Error />;
        }

        function onCPETFormValuesChange({
            value,
            fieldSettings: { id: fieldId },
            setFormValues,
        }: {
            value: Nullable<string | number>;
            fieldSettings: FormFields[number];
            setFormValues: React.Dispatch<
                React.SetStateAction<Nullable<CPETInformation>>
            >;
        }) {
            setFormValues((prevFormValues: Nullable<CPETInformation>) => {
                if (!prevFormValues) {
                    return prevFormValues;
                }

                if (
                    fieldId === "ergometer" &&
                    value !== prevFormValues.ergometer
                ) {
                    return {
                        ...prevFormValues,
                        [fieldId]: value as string,
                        protocol: null,
                    };
                }

                return {
                    ...prevFormValues,
                    [fieldId]: value,
                };
            });
        }

        return (
            <DataInputGenericForm
                formTitle="CPET Information"
                formFields={formFields}
                formData={formValues}
                onFromValuesChange={({ value, fieldSettings }) =>
                    onCPETFormValuesChange({
                        value,
                        fieldSettings,
                        setFormValues,
                    })
                }
                loading={loading || submissionLoading}
            />
        );
    }
);
